import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/hooks/useAppStore';
import { pushEvent } from '@/hooks/useAnalytics';
import { createGeneration, uploadReference } from '@/api/generation';
import type { CreateGenerationRequest } from '@/api/generation';
import { ApiError } from '@/api/client';
import { track } from '@/utils/tracker';
import {
  getGenerationEntryConfig,
  getGenerationPricingQuote,
  modelSelectionMatchesGenerationEntry,
  modelSupportsGenerationEntry,
} from '@/api/config';
import { refreshModelsConfig, useAppConfig } from '@/hooks/useAppConfig';
import { useGenerationTasks } from '@/contexts/generationTaskStore';
import { useEntryAIHelp } from '@/pages/generate/hooks/useEntryAIHelp';

export interface ToolResult {
  imageUrl: string;
  thumbnailUrl?: string;
  styleName: string;
  ratio: string;
  prompt: string;
  gen_id?: string;
}

export interface ToolGenerateOptions {
  styleId?: string;
  colorizeStrength?: number;
  outpaintDirections?: Array<'top' | 'bottom' | 'left' | 'right'>;
}

export interface ToolGenerationControls {
  toolId: string;
  models: NonNullable<ReturnType<typeof useAppConfig>>['models'];
  modelId: string;
  quality: string;
  ratio: string;
  supportedQualities: string[];
  supportedRatios: string[];
  ratioMode: 'selectable' | 'source' | 'fixed';
  fixedRatio?: string | null;
  aiHelpProfile: string | null;
  aiHelpDisabled: boolean;
  aiHelpLoading: boolean;
  aiHelpError: string | null;
  capacityFull: boolean;
  setModelId: (value: string) => void;
  setQuality: (value: string) => void;
  setRatio: (value: string) => void;
}

interface ToolCreateRequestParams {
  prompt: string;
  styleId?: string;
  modelId: string;
  referenceImageUrl?: string;
  secondaryImageUrl?: string;
  idempotencyKey: string;
  toolId: string;
  mode: 'img2img' | 'tryon' | 'text2img';
  entryId: string;
  generationOperation: 'image_generate' | 'image_edit';
  quality: string;
  ratio: string;
  expectedCostPoints: number;
  pricingConfigVersion: number;
  pricingConfigSha256: string;
  options?: ToolGenerateOptions;
}

export function buildToolCreateRequest(params: ToolCreateRequestParams): CreateGenerationRequest {
  return {
    prompt: params.prompt,
    style_id: params.styleId ?? '',
    ratio: params.ratio,
    model_id: params.modelId,
    quality: params.quality,
    expected_cost_points: params.expectedCostPoints,
    pricing_config_version: params.pricingConfigVersion,
    pricing_config_sha256: params.pricingConfigSha256,
    reference_image_url: params.referenceImageUrl,
    secondary_image_url: params.secondaryImageUrl,
    idempotency_key: params.idempotencyKey,
    tool_id: params.toolId,
    mode: params.mode,
    entry_id: params.entryId,
    source_entry: params.entryId,
    generation_operation: params.generationOperation,
    tool_options: params.toolId === 'colorize'
      ? { colorize_strength: params.options?.colorizeStrength }
      : params.toolId === 'outpaint'
        ? { outpaint_directions: params.options?.outpaintDirections }
        : undefined,
  };
}

const toolGenerationModes: Record<string, 'img2img' | 'tryon' | 'text2img'> = {
  bg_clean: 'img2img',
  bg_replace: 'img2img',
  cloth_extract: 'img2img',
  colorize: 'img2img',
  object_remove: 'img2img',
  outpaint: 'img2img',
  style_transfer: 'img2img',
  watermark_remove: 'img2img',
};

function getToolGenerationMode(toolId: string): 'img2img' | 'tryon' | 'text2img' | null {
  return toolGenerationModes[toolId] ?? null;
}

export function useToolGenerate(toolId: string) {
  const { user } = useAppStore();
  const {
    acceptCreateAck,
    applyCapacity,
    activeCount,
    activeLimit,
    capacityReady,
  } = useGenerationTasks();
  const { t } = useTranslation();
  const config = useAppConfig();
  const requestMode = getToolGenerationMode(toolId);
  const entryConfig = getGenerationEntryConfig(config, toolId);
  const {
    aiHelpProfile,
    aiHelpDisabled,
    expandEntryPrompt,
  } = useEntryAIHelp({
    profile: entryConfig?.ai_help_profile,
    mode: toolId,
    toolId,
    user,
  });
  const availableModels = useMemo(
    () => (config?.models ?? []).filter((model) => modelSupportsGenerationEntry(model, entryConfig)),
    [config?.models, entryConfig],
  );
  const [modelId, setModelId] = useState('');
  const [quality, setQuality] = useState('');
  const [ratio, setRatio] = useState('');
  const selectedModel = availableModels.find((model) => model.id === modelId);
  const capacityFull = capacityReady && activeLimit > 0 && activeCount >= activeLimit;

  useEffect(() => {
    if (!availableModels.length) {
      setModelId('');
      return;
    }
    if (!availableModels.some((model) => model.id === modelId)) {
      setModelId(
        availableModels.find((model) => model.id === config?.default_model)?.id
        ?? availableModels[0].id,
      );
    }
  }, [availableModels, config?.default_model, modelId]);

  useEffect(() => {
    if (!selectedModel) {
      setQuality('');
      setRatio('');
      return;
    }
    const qualities = selectedModel.supported_qualities ?? [];
    if (!qualities.includes(quality)) {
      setQuality(qualities.includes('standard') ? 'standard' : (qualities[0] ?? ''));
    }
    if (entryConfig?.ratio_mode === 'fixed' && entryConfig.fixed_ratio) {
      setRatio(entryConfig.fixed_ratio);
      return;
    }
    const ratios = selectedModel.supported_ratios ?? [];
    if (entryConfig?.ratio_mode === 'selectable' && !ratios.includes(ratio)) {
      setRatio(ratios.includes('1:1') ? '1:1' : (ratios[0] ?? ''));
    }
  }, [entryConfig, quality, ratio, selectedModel]);

  const pricingQuote = modelId
    ? getGenerationPricingQuote(config, modelId, quality)
    : null;
  const pointsCost = pricingQuote?.expected_cost_points ?? null;
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<ToolResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aiHelpLoading, setAiHelpLoading] = useState(false);
  const [aiHelpError, setAiHelpError] = useState<string | null>(null);
  const lastPromptRef = useRef('');
  const lastImageRef = useRef<string | undefined>(undefined);
  const lastSecondaryImageRef = useRef<string | undefined>(undefined);
  const lastOptionsRef = useRef<ToolGenerateOptions | undefined>(undefined);

  const uploadDataUrlIfNeeded = useCallback(async (
    imageDataUrl?: string,
    fallbackName = 'ref.jpg',
  ): Promise<{ url?: string; recommendedRatio?: string }> => {
    if (!imageDataUrl) return {};
    if (!imageDataUrl.startsWith('data:')) return { url: imageDataUrl };
    const blob = await fetch(imageDataUrl).then((r) => r.blob());
    const file = new File([blob], fallbackName, { type: blob.type || 'image/jpeg' });
    const uploadResult = await uploadReference(file);
    if (entryConfig?.ratio_mode === 'source' && uploadResult.recommended_ratio) {
      setRatio(uploadResult.recommended_ratio);
    }
    return { url: uploadResult.url, recommendedRatio: uploadResult.recommended_ratio ?? undefined };
  }, [entryConfig?.ratio_mode]);

  const doGenerate = useCallback(async (
    prompt: string,
    referenceImageDataUrl?: string,
    secondaryImageDataUrl?: string,
    options?: ToolGenerateOptions,
  ) => {
    if (!user) return;
    if (capacityFull) {
      setError(t('gen_concurrencyLimitReached', '并发任务已满，请等待已有任务完成'));
      return;
    }
    if (!modelId || !requestMode || !pricingQuote) {
      setError(t('gen_configUnavailable', '生成配置暂不可用，请稍后重试'));
      return;
    }
    if ((user.points ?? 0) < pointsCost) {
      setError(t('gen_pointsInsufficient'));
      return;
    }

    setIsGenerating(true);
    setResult(null);
    setError(null);
    const generationPrompt = prompt.trim();
    lastPromptRef.current = prompt;
    lastImageRef.current = referenceImageDataUrl;
    lastSecondaryImageRef.current = secondaryImageDataUrl;
    lastOptionsRef.current = options;
    const entryId = `tool_${toolId}`;
    const generationOperation = requestMode === 'text2img' ? 'image_generate' : 'image_edit';
    const reportPayload = {
      entry_id: entryId,
      mode: requestMode,
      model: modelId,
      quality,
      generation_operation: generationOperation,
      source: entryId,
      has_reference: !!referenceImageDataUrl,
      image_count: [referenceImageDataUrl, secondaryImageDataUrl].filter(Boolean).length,
    };
    pushEvent('tool_generate_start', user?.userId, { toolId, pointsCost });
    track.generateStart({
      mode: requestMode,
      model: modelId,
      has_reference: !!referenceImageDataUrl,
      image_count: [referenceImageDataUrl, secondaryImageDataUrl].filter(Boolean).length,
      source: entryId,
      entry_id: entryId,
      generation_operation: generationOperation,
    });
    track.generateSubmit(reportPayload);
    try {
      const [refUpload, secondaryUpload] = await Promise.all([
        uploadDataUrlIfNeeded(referenceImageDataUrl, 'ref.jpg'),
        uploadDataUrlIfNeeded(secondaryImageDataUrl, 'secondary.jpg'),
      ]);
      const effectiveRatio = entryConfig?.ratio_mode === 'source'
        ? (refUpload.recommendedRatio ?? ratio)
        : ratio;
      if (!modelSelectionMatchesGenerationEntry(
        selectedModel,
        entryConfig,
        quality,
        effectiveRatio,
        [refUpload.url, secondaryUpload.url].filter(Boolean).length,
      )) {
        setError(t('gen_configUnavailable', '生成配置暂不可用，请稍后重试'));
        return;
      }
      const idempotencyKey = `tool_${toolId}_${Date.now()}_${Math.random().toString(36).slice(2)}`;

      const requestPayload = buildToolCreateRequest({
        prompt: generationPrompt,
        styleId: options?.styleId,
        modelId,
        referenceImageUrl: refUpload.url,
        secondaryImageUrl: secondaryUpload.url,
        idempotencyKey,
        toolId,
        mode: requestMode,
        entryId,
        generationOperation,
        quality,
        ratio: effectiveRatio,
        expectedCostPoints: pricingQuote.expected_cost_points,
        pricingConfigVersion: pricingQuote.pricing_config_version,
        pricingConfigSha256: pricingQuote.pricing_config_sha256,
        options,
      });
      const createResult = await createGeneration(requestPayload);

      if (createResult.status === 'failed' || createResult.status === 'blocked') {
        throw new Error('Generation failed');
      }
      acceptCreateAck(createResult, requestPayload);
      pushEvent('tool_generate_accepted', user?.userId, { toolId, pointsCost });
    } catch (err) {
      console.error('Tool generation error:', err);
      let pricingConfigRefreshed = true;
      if (err instanceof ApiError && err.code === 'CONFIG_STALE') {
        pricingConfigRefreshed = await refreshModelsConfig();
      }
      if (err instanceof ApiError && err.code === 'GEN_ACTIVE_LIMIT') {
        const nextActiveCount = Number(err.details?.active_count);
        const nextActiveLimit = Number(err.details?.active_limit);
        if (Number.isFinite(nextActiveCount) && Number.isFinite(nextActiveLimit)) {
          applyCapacity(nextActiveCount, nextActiveLimit);
        }
      }
      track.generateFail(err instanceof Error ? err.message : 'tool_generation_error', { source: `tool_${toolId}` });
      if (err instanceof ApiError) {
        track.generationFailed(err.code, reportPayload);
      } else if (err instanceof Error) {
        track.generationFailed(err.message || 'tool_generation_error', reportPayload);
      } else {
        track.generationFailed('tool_generation_error', reportPayload);
      }
      pushEvent('tool_generate_error', user?.userId, { toolId, error: String(err) });
      const message = err instanceof ApiError && err.code === 'CONFIG_STALE'
        ? pricingConfigRefreshed
          ? t('gen_pricingConfigUpdated', '积分配置已更新，请确认最新积分后重新提交')
          : t('gen_pricingConfigRefreshFailed', '积分配置刷新失败，本次任务未提交，请稍后重试')
        : err instanceof ApiError && err.code === 'GEN_TIMEOUT'
        ? t('gen_generationContinuesInBackground', '生成仍在后台进行，请稍后回来查看')
        : err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t('gen_genFailed');
      setError(message || t('gen_genFailed'));
    } finally {
      setIsGenerating(false);
    }
  }, [acceptCreateAck, applyCapacity, capacityFull, entryConfig, modelId, pointsCost, pricingQuote, quality, ratio, requestMode, selectedModel, t, toolId, uploadDataUrlIfNeeded, user]);

  const doRegenerate = useCallback(() => {
    if (lastPromptRef.current || lastImageRef.current || lastSecondaryImageRef.current) {
      setResult(null);
      doGenerate(
        lastPromptRef.current,
        lastImageRef.current,
        lastSecondaryImageRef.current,
        lastOptionsRef.current,
      );
    }
  }, [doGenerate]);

  const doCancel = useCallback(() => {
    setIsGenerating(false);
    setError(t('gen_generationContinuesInBackground', '任务已提交后会继续在后台运行'));
  }, [t]);

  const clearError = useCallback(() => setError(null), []);
  const expandToolPrompt = useCallback(async (currentPrompt: string) => {
    setAiHelpLoading(true);
    setAiHelpError(null);
    try {
      return await expandEntryPrompt(currentPrompt);
    } catch (err) {
      setAiHelpError(
        err instanceof ApiError
          ? err.message
          : t('gen_aiHelpFailed', 'AI 帮写失败，请稍后重试'),
      );
      throw err;
    } finally {
      setAiHelpLoading(false);
    }
  }, [expandEntryPrompt, t]);

  useEffect(() => {
    setAiHelpError(null);
  }, [aiHelpProfile, toolId, user?.userId]);

  return {
    isGenerating,
    result,
    error,
    doGenerate,
    doRegenerate,
    doCancel,
    clearError,
    expandToolPrompt,
    user,
    pointsCost,
    controls: {
      toolId,
      models: availableModels,
      modelId,
      quality,
      ratio,
      supportedQualities: selectedModel?.supported_qualities ?? [],
      supportedRatios: selectedModel?.supported_ratios ?? [],
      ratioMode: entryConfig?.ratio_mode ?? 'source',
      fixedRatio: entryConfig?.fixed_ratio,
      aiHelpProfile,
      aiHelpDisabled,
      aiHelpLoading,
      aiHelpError,
      capacityFull,
      setModelId,
      setQuality,
      setRatio,
    } satisfies ToolGenerationControls,
  };
}
